// Pure, dependency-free grouping + rate-limit logic for client Bugsnag reporting.
// Extracted from bugsnag.js so it can be unit-tested without loading the Bugsnag SDK.

const ERROR_GROUP_HOUR_STR_LEN = 13; // 'YYYY-MM-DDTHH' → one bucket per calendar hour
const ERROR_GROUP_DAY_STR_LEN = 10; //  'YYYY-MM-DD'    → one bucket per calendar day

// The bucket an event belongs to = the exact key Bugsnag groups by. For store-API errors
// we set groupingHash to the _errorName, so the rate limit keys off the SAME bucket the
// Bugsnag dashboard shows (network errors collapse; server errors stay split by path).
export function getErrorGroupKey(event) {
  if (event.groupingHash) {
    return String(event.groupingHash);
  }

  const first = event.errors && event.errors[0];

  if (!first) {
    return 'unknown';
  }

  const frame = first.stacktrace && first.stacktrace[0];
  const loc = frame ? `${frame.file || ''}:${frame.lineNumber ?? ''}` : '';

  return `${first.errorClass || 'Error'}\n${first.errorMessage || ''}\n${loc}`;
}

function windowCount(counts, bucketKey) {
  const entry = counts.get(bucketKey);

  return entry ? entry.count : 0;
}

function bumpWindow(counts, bucketKey) {
  const entry = counts.get(bucketKey);

  if (entry) {
    entry.count += 1;
  } else {
    counts.set(bucketKey, { count: 1 });
  }
}

// Dual-window (hour + day) limiter with its own encapsulated state. Keep an event only if
// BOTH windows are still under their cap, then count it in each — a dropped event consumes
// no budget, so the hourly cap can never eat into the daily one (clean "≤ N/hour AND
// ≤ M/day"). A non-positive/absent limit disables that window. `nowIso` is injectable for
// deterministic tests; production passes nothing and uses the current time.
export function createErrorRateLimiter() {
  const hourlyCounts = new Map();
  const dailyCounts = new Map();

  function shouldDiscard(event, hourlyLimit, dailyLimit, nowIso = new Date().toISOString()) {
    const groupKey = getErrorGroupKey(event);
    const hourOn = typeof hourlyLimit === 'number' && hourlyLimit > 0;
    const dayOn = typeof dailyLimit === 'number' && dailyLimit > 0;
    const hourKey = `${nowIso.slice(0, ERROR_GROUP_HOUR_STR_LEN)}:${groupKey}`;
    const dayKey = `${nowIso.slice(0, ERROR_GROUP_DAY_STR_LEN)}:${groupKey}`;

    if (hourOn && windowCount(hourlyCounts, hourKey) >= hourlyLimit) {
      return true;
    }
    if (dayOn && windowCount(dailyCounts, dayKey) >= dailyLimit) {
      return true;
    }
    if (hourOn) {
      bumpWindow(hourlyCounts, hourKey);
    }
    if (dayOn) {
      bumpWindow(dailyCounts, dayKey);
    }

    return false;
  }

  return { shouldDiscard };
}
